import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';

/**
 * The product name and logo that user interfaces show for the Salesforce
 * integration. WealthHub is a Salesforce OEM product, so a tenant can show its
 * Salesforce connection as WealthHub. Branding changes only user-facing copy
 * and logos. Salesforce URLs, object API names, and API identifiers keep their
 * Salesforce names.
 */
export interface SalesforceBrand {
  name: 'Salesforce' | 'WealthHub';
  /** Path for `getPublicImageUrl`. */
  logoPath: string;
  /** The logo is a wordmark that already shows the name. */
  logoShowsName: boolean;
}

export const SALESFORCE_BRAND: SalesforceBrand = {
  name: 'Salesforce',
  logoPath: '/salesforceLogo.svg',
  logoShowsName: false,
};

/** The official WealthHub logo from https://wealthhubsolutions.com/. */
export const WEALTHHUB_BRAND: SalesforceBrand = {
  name: 'WealthHub',
  logoPath: '/wealthHubLogo.png',
  logoShowsName: true,
};

/**
 * Returns the tenant's Salesforce integration brand. Without an integration
 * configuration, the tenant has no Salesforce connection to rebrand, so the
 * result is Salesforce.
 */
export function useSalesforceBrand(): SalesforceBrand {
  const { integrationConfiguration } = useTenantDetailsContext();
  return integrationConfiguration?.salesforceIsWealthhub
    ? WEALTHHUB_BRAND
    : SALESFORCE_BRAND;
}
